import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  listarLicitaciones,
  guardarLicitacion,
  importarLicitacionesCSV,
  eliminarLicitacionesCerradas,
} from "../api";
import {
  HiPlus,
  HiUpload,
  HiDownload,
  HiTrash,
  HiSelector,
  HiSortAscending,
  HiSortDescending,
} from "react-icons/hi";
import LicitacionCard from "./LicitacionCard";
import ModalLicitacion from "./ModalLicitacion";
import SkeletonCard from "./SkeletonCard";
import { toast } from "react-toastify";
import "../styles.css";
import logo from "../assets/logo.png";

function Dashboard({
  filtroEstado = "",
  filtroPais = "",
  busquedaGeneral = "",
  usuario,
  onActualizarGlobal,
  triggerActualizar,
  soloFavoritos = false,
}) {
  const [licitaciones, setLicitaciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [seleccionada, setSeleccionada] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [currentUser] = useState(usuario);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const fileInputRef = useRef(null);

  const fetchLicitaciones = async () => {
    if (!usuario) return;
    try {
      setCargando(true);
      const data = await listarLicitaciones();
      setLicitaciones(data || []);
    } catch (err) {
      console.error("Error cargando licitaciones:", err);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    fetchLicitaciones();
  }, [usuario, triggerActualizar]);

  useEffect(
    () => setPage(0),
    [filtroEstado, filtroPais, busquedaGeneral, pageSize, sortConfig]
  );

  const calcularEstado = (lic) => {
    const hoy = new Date();
    const fecha = lic.fecha_presentacion
      ? new Date(lic.fecha_presentacion)
      : null;
    const diasRestantes = fecha
      ? Math.ceil((fecha - hoy) / (1000 * 60 * 60 * 24))
      : null;
    const estadoOriginal = (lic.estado || "pendiente").toLowerCase();

    if (estadoOriginal === "adjudicada") return "adjudicada";
    if (estadoOriginal === "no adjudicada") return "no adjudicada";
    if (estadoOriginal === "presentada") return "presentada";

    if (estadoOriginal === "pendiente") {
      const hoyNormalizado = new Date();
      hoyNormalizado.setHours(0, 0, 0, 0);

      const fechaNormalizada = fecha ? new Date(fecha) : null;
      if (fechaNormalizada) fechaNormalizada.setHours(0, 0, 0, 0);

      if (
        fechaNormalizada &&
        fechaNormalizada.getTime() === hoyNormalizado.getTime()
      )
        return "cierra hoy";
      if (fechaNormalizada && fechaNormalizada < hoyNormalizado)
        return "cerrada";
      if (diasRestantes !== null && diasRestantes <= 7) return "proxima";
      return "pendiente";
    }
    return estadoOriginal;
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        if (prev.direction === "asc") return { key, direction: "desc" };
        return { key: null, direction: "asc" }; // Tercer clic restaura el orden inteligente por defecto
      }
      // Para presupuesto, por defecto el primer clic muestra de mayor a menor
      const initialDir = key === "presupuesto" ? "desc" : "asc";
      return { key, direction: initialDir };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <HiSelector className="sort-icon inactive" size={15} />;
    }
    return sortConfig.direction === "asc" ? (
      <HiSortAscending className="sort-icon active" size={15} />
    ) : (
      <HiSortDescending className="sort-icon active" size={15} />
    );
  };

  const handleEliminarCerradas = async () => {
    if (currentUser?.rol !== "admin") return;

    const confirmar = window.confirm(
      "⚠️ Esta acción eliminará TODAS las licitaciones cerradas.\n\n¿Continuar?"
    );
    if (!confirmar) return;

    try {
      const result = await eliminarLicitacionesCerradas();
      toast.success(
        `Eliminadas ${result.eliminadas ?? 0} licitaciones cerradas`
      );
      await fetchLicitaciones();
      if (typeof onActualizarGlobal === "function") onActualizarGlobal();
    } catch (err) {
      console.error(err);
      toast.error("Error eliminando licitaciones cerradas");
    }
  };

  const licitacionesFiltradas = useMemo(() => {
    const filtradas = licitaciones
      .filter((l) => {
        const estadoOriginal = (l.estado || "").toLowerCase();
        if (
          estadoOriginal === "pendiente de aceptar" ||
          estadoOriginal === "rechazada"
        ) {
          return false;
        }

        const estado = calcularEstado(l);

        if (currentUser?.rol !== "admin") {
          if (["cerrada", "adjudicada", "no adjudicada"].includes(estado))
            return false;
        }

        if (!filtroEstado) return true;

        if (filtroEstado.toLowerCase() === "pendiente") {
          return (
            estado === "pendiente" ||
            estado === "proxima" ||
            estado === "cierra hoy"
          );
        }

        return estado === filtroEstado.toLowerCase();
      })
      .filter(
        (l) =>
          !filtroPais ||
          (l.pais || "").trim().toLowerCase() ===
            filtroPais.trim().toLowerCase()
      )
      .filter((l) => !soloFavoritos || l.favorito)
      .filter((l) => {
        if (!busquedaGeneral) return true;
        const texto = busquedaGeneral.toLowerCase();
        const expediente = (l.numero_expediente || "").toLowerCase();
        const titulo = (l.titulo || "").toLowerCase();
        const organismo = (l.organismo || "").toLowerCase();
        const presupuesto =
          l.presupuesto != null ? l.presupuesto.toString() : "";
        const fecha = l.fecha_presentacion
          ? new Date(l.fecha_presentacion).toISOString().split("T")[0]
          : "";
        return (
          expediente.includes(texto) ||
          titulo.includes(texto) ||
          organismo.includes(texto) ||
          presupuesto.includes(texto) ||
          fecha.includes(texto)
        );
      });

    const hoyNormalizado = new Date();
    hoyNormalizado.setHours(0, 0, 0, 0);

    return [...filtradas].sort((a, b) => {
      if (!sortConfig.key) {
        // Orden inteligente por defecto: próximas a vencer primero, luego cerradas
        const fa = a.fecha_presentacion
          ? new Date(a.fecha_presentacion)
          : null;
        const fb = b.fecha_presentacion
          ? new Date(b.fecha_presentacion)
          : null;

        if (!fa && !fb) return 0;
        if (!fa) return 1;
        if (!fb) return -1;

        const aFutura = fa >= hoyNormalizado;
        const bFutura = fb >= hoyNormalizado;

        if (aFutura && !bFutura) return -1;
        if (!aFutura && bFutura) return 1;

        return fa - fb;
      }

      const dir = sortConfig.direction === "asc" ? 1 : -1;
      const key = sortConfig.key;

      if (key === "presupuesto") {
        const va = a.presupuesto != null ? Number(a.presupuesto) : null;
        const vb = b.presupuesto != null ? Number(b.presupuesto) : null;
        if (va === null && vb === null) return 0;
        if (va === null) return 1;
        if (vb === null) return -1;
        return (va - vb) * dir;
      }

      if (key === "fecha_presentacion") {
        const fa = a.fecha_presentacion
          ? new Date(a.fecha_presentacion).getTime()
          : null;
        const fb = b.fecha_presentacion
          ? new Date(b.fecha_presentacion).getTime()
          : null;
        if (fa === null && fb === null) return 0;
        if (fa === null) return 1;
        if (fb === null) return -1;
        return (fa - fb) * dir;
      }

      if (key === "estado") {
        const ea = calcularEstado(a);
        const eb = calcularEstado(b);
        return ea.localeCompare(eb, "es", { sensitivity: "base" }) * dir;
      }

      const sa = String(a[key] || "").trim();
      const sb = String(b[key] || "").trim();
      if (!sa && !sb) return 0;
      if (!sa) return 1;
      if (!sb) return -1;
      return (
        sa.localeCompare(sb, "es", { numeric: true, sensitivity: "base" }) *
        dir
      );
    });
  }, [
    licitaciones,
    filtroEstado,
    filtroPais,
    busquedaGeneral,
    currentUser,
    soloFavoritos,
    sortConfig,
  ]);

  const totalItems = licitacionesFiltradas.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = page * pageSize;
  const end = start + pageSize;
  const currentPageItems = licitacionesFiltradas.slice(start, end);

  const goPrev = () => setPage((p) => Math.max(0, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));
  const goTo = (p) => setPage(Math.max(0, Math.min(totalPages - 1, p)));

  const handleGuardarNueva = async (nueva) => {
    if (!currentUser || currentUser.rol !== "admin") return;
    try {
      const licToSave = {
        ...nueva,
        presupuesto: nueva.presupuesto ? parseFloat(nueva.presupuesto) : 0,
      };
      await guardarLicitacion(licToSave);
      toast.success("Licitación guardada correctamente");
      if (typeof onActualizarGlobal === "function") onActualizarGlobal();
      setShowModal(false);
    } catch (err) {
      console.error("Error guardando licitación:", err);
      toast.error("Error al guardar la licitación");
    }
  };

  const handleCsvChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await importarLicitacionesCSV(file);
      const msg = `Importación completada: Creadas: ${
        result.creadas || 0
      }, Actualizadas: ${result.actualizadas || 0}`;
      toast.success(msg);

      await fetchLicitaciones();
      if (typeof onActualizarGlobal === "function") onActualizarGlobal();
    } catch (err) {
      console.error("Error procesando CSV:", err);
      toast.error("Error importando CSV: " + err.message);
    } finally {
      e.target.value = "";
    }
  };

  const abrirSelectorCsv = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleExportCsv = () => {
    const pendientes = licitacionesFiltradas.filter((l) => {
      const estado = calcularEstado(l);
      return estado === "pendiente" || estado === "proxima";
    });

    if (!pendientes.length) {
      toast.warning("No hay licitaciones pendientes para exportar.");
      return;
    }

    const headers = [
      "numero_expediente",
      "titulo",
      "organismo",
      "pais",
      "fecha_presentacion",
      "presupuesto",
      "estado",
    ];

    const rows = pendientes.map((l) => [
      l.numero_expediente ?? "",
      (l.titulo ?? "").replace(/\"/g, '""'),
      (l.organismo ?? "").replace(/\"/g, '""'),
      l.pais ?? "",
      l.fecha_presentacion ?? "",
      l.presupuesto ?? "",
      calcularEstado(l),
    ]);

    const csvContent =
      headers.join(";") +
      "\n" +
      rows
        .map((r) =>
          r
            .map((field) => `"${String(field).replace(/\n/g, " ")}"`)
            .join(";")
        )
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "licitaciones_pendientes.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="dashboard-container animate-fadeIn">
      <div className="dashboard-header">
        <h1>Licitaciones</h1>
        <img src={logo} alt="Logo empresa" className="dashboard-logo" />
      </div>

      {currentUser?.rol === "admin" && (
        <div className="dashboard-actions">
          <button className="btn-nueva" onClick={() => setShowModal(true)}>
            <HiPlus size={20} />
            Nueva licitación
          </button>

          <button
            className="btn-nueva"
            type="button"
            onClick={abrirSelectorCsv}
          >
            <HiUpload size={20} />
            Cargar CSV
          </button>

          <button
            className="btn-nueva"
            type="button"
            onClick={handleExportCsv}
          >
            <HiDownload size={20} />
            Exportar
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={handleCsvChange}
          />

          <button
            className="btn-nueva botonRojo"
            type="button"
            onClick={handleEliminarCerradas}
          >
            <HiTrash size={20} />
            Limpiar cerradas
          </button>
        </div>
      )}

      <div className="lista-encabezado">
        <div
          className={`col-expediente sortable-col ${
            sortConfig.key === "numero_expediente" ? "sorted-active" : ""
          }`}
          onClick={() => handleSort("numero_expediente")}
          title="Ordenar por Expediente"
        >
          <span>Expediente</span>
          {renderSortIcon("numero_expediente")}
        </div>
        <div
          className={`col-titulo sortable-col ${
            sortConfig.key === "titulo" ? "sorted-active" : ""
          }`}
          onClick={() => handleSort("titulo")}
          title="Ordenar por Título"
        >
          <span>Título</span>
          {renderSortIcon("titulo")}
        </div>
        <div
          className={`col-organismo sortable-col ${
            sortConfig.key === "organismo" ? "sorted-active" : ""
          }`}
          onClick={() => handleSort("organismo")}
          title="Ordenar por Organismo"
        >
          <span>Organismo</span>
          {renderSortIcon("organismo")}
        </div>
        <div
          className={`col-presupuesto sortable-col ${
            sortConfig.key === "presupuesto" ? "sorted-active" : ""
          }`}
          onClick={() => handleSort("presupuesto")}
          title="Ordenar por Presupuesto"
        >
          <span>Presupuesto</span>
          {renderSortIcon("presupuesto")}
        </div>
        <div
          className={`col-fecha sortable-col ${
            sortConfig.key === "fecha_presentacion" ? "sorted-active" : ""
          }`}
          onClick={() => handleSort("fecha_presentacion")}
          title="Ordenar por Fecha de Cierre"
        >
          <span>Fecha Cierre</span>
          {renderSortIcon("fecha_presentacion")}
        </div>
        <div
          className={`col-estado sortable-col ${
            sortConfig.key === "estado" ? "sorted-active" : ""
          }`}
          onClick={() => handleSort("estado")}
          title="Ordenar por Estado"
        >
          <span>Estado</span>
          {renderSortIcon("estado")}
        </div>
        <div className="col-boton">Acciones</div>
      </div>

      <div className="lista-licitaciones">
        {cargando ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            {currentPageItems.map((lic) => (
              <LicitacionCard
                key={lic.numero_expediente}
                licitacion={lic}
                seleccionada={seleccionada === lic.numero_expediente}
                onSelect={() => setSeleccionada(lic.numero_expediente)}
                onActualizar={onActualizarGlobal}
                usuario={currentUser}
              />
            ))}
            {currentPageItems.length === 0 && (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: "var(--text-secondary)",
                  background: "rgba(255,255,255,0.02)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                No se han encontrado licitaciones que coincidan con los filtros.
              </div>
            )}
          </>
        )}
      </div>

      <div className="paginacion-container">
        <div className="paginacion-left">
          <label>
            Mostrar:
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setPage(0);
              }}
              style={{ marginLeft: 5 }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </label>
        </div>

        <div className="paginacion-numeros">
          <button onClick={goPrev} disabled={page === 0}>
            Anterior
          </button>

          {page > 2 && (
            <>
              <button onClick={() => goTo(0)}>1</button>
              {page > 3 && <span>...</span>}
            </>
          )}

          {Array.from({ length: totalPages })
            .map((_, idx) => idx)
            .filter((idx) => Math.abs(idx - page) <= 2)
            .map((idx) => (
              <button
                key={idx}
                className={idx === page ? "activo" : ""}
                onClick={() => goTo(idx)}
              >
                {idx + 1}
              </button>
            ))}

          {page < totalPages - 3 && (
            <>
              {page < totalPages - 4 && <span>...</span>}
              <button onClick={() => goTo(totalPages - 1)}>{totalPages}</button>
            </>
          )}

          <button onClick={goNext} disabled={page === totalPages - 1}>
            Siguiente
          </button>
        </div>

        <div className="paginacion-right" />
      </div>

      {showModal && currentUser?.rol === "admin" && (
        <ModalLicitacion
          onClose={() => setShowModal(false)}
          onGuardar={handleGuardarNueva}
        />
      )}
    </div>
  );
}

export default Dashboard;
